// Vendor libraries
import { ElementRef, EventEmitter } from '@angular/core';
import * as i0 from "@angular/core";
/**
 * Infinite Scroll Directive
 *
 * Handles logic to determine when additional items should be fetched for any list of items that is desired to be infinitely scrollable.
 *
 * Usage:
 *      This directive should be attached to an empty <div> element below the element where *ngFor is used to populate the DOM with a list of items.
 *  When a user scrolls to the bottom of the list, this <div> element will enter the viewport, triggering fetchMoreItemsHandler.
 *  The parent component should make a request to get additional items in the handler function, then append them to the array that is referenced in
 *  the *ngFor statement. The DOM will be updated with new items, pushing the host element of this directive outside the viewport once more.
 *  When the user scrolls to the bottom of the new items the process repeats, creating an infinite scroll effect.
 *
 * Output Properties:
 *  fetchMoreItemsHandler: emits when the host element enters the viewport. The parent component should fetch more items in response to this event
 *                         and append them to the array of items that is desired to be infinitely scrollable.
 *
 * Long viewport support:
 *  Some pages can have a viewport tall enough that one page of data does not push the sentinel out of view.
 *  In that scenario, call `scheduleViewportRecheck` after each successful fetch completion to conditionally
 *  trigger the next fetch while there are more pages.
 *
 * Example in the parent component:
 *  @ViewChild(InfiniteScrollDirective) infiniteScrollDirective?: InfiniteScrollDirective;
 *
 *  fetchNextPageIfSentinelStillVisible = () => {
 *      this.infiniteScrollDirective?.scheduleViewportRecheck(() => {
 *          if (this.loading || !this.hasMorePages()) return;
 *          this.fetchMoreItems();
 *      });
 *  }
 *
 * The check for more pages is necessary because component state can change before the scheduled recheck runs.
 */
export class InfiniteScrollDirective {
    constructor(el) {
        this.el = el;
        this.fetchMoreItemsHandler = new EventEmitter();
        this.isHostElementInViewport = () => {
            const rect = this.el.nativeElement.getBoundingClientRect();
            return rect.top < window.innerHeight && rect.bottom > 0;
        };
        // Recheck on the next macrotask so layout reflects newly rendered rows.
        this.scheduleViewportRecheck = (onVisibleInViewport) => setTimeout(() => {
            if (!this.isHostElementInViewport())
                return;
            onVisibleInViewport();
        }, 0);
    }
    ngAfterViewInit() {
        // Make sure the element that triggers infinite scroll has a minimum height so that is visible
        // to the browser and triggers the `fetchMoreItemsHandler`.
        if (!this.el.nativeElement.style.minHeight) {
            this.el.nativeElement.style.minHeight = '1px';
        }
        this.observer = new window.IntersectionObserver(([infiniteScrollTarget]) => {
            if (infiniteScrollTarget.isIntersecting) {
                this.fetchMoreItemsHandler.emit();
            }
        });
        this.observer.observe(this.el.nativeElement);
    }
    ngOnDestroy() {
        return this.observer?.disconnect();
    }
}
InfiniteScrollDirective.ɵfac = function InfiniteScrollDirective_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || InfiniteScrollDirective)(i0.ɵɵdirectiveInject(i0.ElementRef)); };
InfiniteScrollDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: InfiniteScrollDirective, selectors: [["", "ycnInfiniteScroll", ""]], outputs: { fetchMoreItemsHandler: "fetchMoreItemsHandler" }, standalone: false });
